import { Chip, ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import {
  TsrctCaseCancellationReason,
  TsrctNegotiationCaseProcessStatus,
  TsrctNegotiationCaseStatus,
} from '@/generated/gql/graphql';

export type NegotiationCaseProcessStatusUnion = `${TsrctNegotiationCaseProcessStatus}`;
export type NegotiationCaseTechnicalStatusUnion = `${TsrctNegotiationCaseStatus}`;

const propsByStatusMap: {
  [key in NegotiationCaseProcessStatusUnion]: { color: ChipProps['color'] };
} = {
  [TsrctNegotiationCaseProcessStatus.OPEN]: { color: 'default' },
  [TsrctNegotiationCaseProcessStatus.IN_PROGRESS]: { color: 'primary' },
  [TsrctNegotiationCaseProcessStatus.COMPLETED]: { color: 'success' },
  [TsrctNegotiationCaseProcessStatus.CANCELLED]: { color: 'error' },
  [TsrctNegotiationCaseProcessStatus.FAILED]: { color: 'error' },
  [TsrctNegotiationCaseProcessStatus.INVITATION_SENT]: { color: 'primary' },
  [TsrctNegotiationCaseProcessStatus.NEGOTIATION_SUCCESSFUL]: { color: 'success' },
};

export interface NegotiationProcessStatusChipProps {
  className?: string;
  processStatus?: Union.Nullable<NegotiationCaseProcessStatusUnion>;
  status?: Union.Nullable<NegotiationCaseTechnicalStatusUnion>;
  cancellationReason?: Union.Nullable<TsrctCaseCancellationReason | `${TsrctCaseCancellationReason}`>;
  cancellationReasonText?: Union.Nullable<string>;
}

export const NegotiationProcessStatusChip = ({
  className,
  status,
  processStatus,
  cancellationReason,
  cancellationReasonText,
}: NegotiationProcessStatusChipProps) => {
  const { t } = useTranslation();

  if (processStatus && status) {
    const { color } = propsByStatusMap[processStatus];
    const baseTitle = t(`NegotiationStatus.${status}`);
    const isCancelled = processStatus === TsrctNegotiationCaseProcessStatus.CANCELLED;
    const reasonLabel = cancellationReason ? t(`CaseCancellationReason.${cancellationReason}`) : null;
    const title =
      isCancelled && reasonLabel
        ? `${reasonLabel}${cancellationReasonText ? ` — ${cancellationReasonText}` : ''}`
        : baseTitle;

    return (
      <Chip
        className={className}
        label={t(`NegotiationProcessStatus.${processStatus}`)}
        color={color}
        data-intercom-target="Negotiation/StatusChip"
        data-testid="Negotiation/StatusChip"
        title={title}
      />
    );
  }

  return null;
};
